import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  CssBaseline,
  Typography,
  Card,
  CardContent,
  CardMedia,
  CardHeader,
  IconButton,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Collapse,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Link,
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';

// Icons
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import LoopIcon from '@mui/icons-material/Loop';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import UndoIcon from '@mui/icons-material/Undo';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';

// dnd-kit imports
import { DndContext, closestCenter } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// ID3 tags reader
import jsmediatags from 'jsmediatags';

/**
 * Format seconds to M:SS.
 */
function formatTime(seconds) {
  if (!seconds || isNaN(seconds)) return '--:--';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60)
    .toString()
    .padStart(2, '0');
  return `${mins}:${secs}`;
}

/**
 * SortableItem component using dnd-kit.
 */
function SortableItem({ id, track, index, currentTrackIndex, handlePlayTrack, toggleSkip, openAssistantPrompt, assistantType }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    width: '100%',
  };
  const textStyles = track.skip ? { textDecoration: 'line-through', color: 'red' } : {};
  const secondaryText = (
    <>
      {track.artist}
      {track.skip && (
        <>
          <br />
          <Typography variant="caption" color="error">
            This song will be skipped during "Play All."
          </Typography>
        </>
      )}
    </>
  );
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <ListItem
        button
        onClick={() => handlePlayTrack(index, true)}
        sx={{
          bgcolor: index === currentTrackIndex ? 'action.selected' : 'inherit',
          borderLeft: index === currentTrackIndex ? '4px solid #0d6efd' : 'none',
          fontWeight: index === currentTrackIndex ? 600 : 'normal',
        }}
      >
        <Box sx={{ mr: 1, color: 'text.secondary', cursor: 'grab' }} {...listeners}>
          <DragIndicatorIcon />
        </Box>
        <ListItemText
          primary={<Typography variant="body1" sx={textStyles}>{track.title}</Typography>}
          secondary={<Typography variant="body2" sx={textStyles}>{secondaryText}</Typography>}
        />
        <ListItemSecondaryAction sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {track.appleMusicUrl && (
            <Tooltip title={`Listen to ${track.title} on Apple Music`}>
              <IconButton
                component="a"
                href={track.appleMusicUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
              >
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    bgcolor: 'white',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Box component="img" src="/logos/apple-music.svg" alt="Apple Music" sx={{ width: 20, height: 20 }} />
                </Box>
              </IconButton>
            </Tooltip>
          )}
          {track.spotifyUrl && (
            <Tooltip title={`Listen to ${track.title} on Spotify`}>
              <IconButton
                component="a"
                href={track.spotifyUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
              >
                <Box component="img" src="/logos/spotify.svg" alt="Spotify" sx={{ width: 20, height: 20 }} />
              </IconButton>
            </Tooltip>
          )}
          {track.pandoraUrl && (
            <Tooltip title={`Listen to ${track.title} on Pandora`}>
              <IconButton
                component="a"
                href={track.pandoraUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
              >
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    bgcolor: 'white',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Box component="img" src="/logos/pandora.svg" alt="Pandora" sx={{ width: 20, height: 20 }} />
                </Box>
              </IconButton>
            </Tooltip>
          )}
          {track.youtubeMusicUrl && (
            <Tooltip title={`Listen to ${track.title} on YouTube Music`}>
              <IconButton
                component="a"
                href={track.youtubeMusicUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
              >
                <Box component="img" src="/logos/youtube-music.svg" alt="YouTube Music" sx={{ width: 20, height: 20 }} />
              </IconButton>
            </Tooltip>
          )}
          {track.amazonMusicUrl && (
            <Tooltip title={`Listen to ${track.title} on Amazon Music`}>
              <IconButton
                component="a"
                href={track.amazonMusicUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
              >
                <Box component="img" src="/logos/amazon-music.svg" alt="Amazon Music" sx={{ height: 20, width: 'auto' }} />
              </IconButton>
            </Tooltip>
          )}
          {assistantType && track.siriTitle && (
            <Tooltip
              title={
                assistantType === 'siri'
                  ? `Just say \"Hey Siri, listen to ${track.siriTitle} by ${track.artist}\" to play on Apple Music`
                  : `Just say \"Hey Google, play ${track.siriTitle} by ${track.artist}\"`
              }
            >
              <IconButton
                sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
                onClick={(e) => {
                  e.stopPropagation();
                  openAssistantPrompt(track);
                }}
              >
                <Box
                  component="img"
                  src={assistantType === 'siri' ? '/logos/siri.svg' : '/logos/google-assistant.svg'}
                  alt={assistantType === 'siri' ? 'Siri' : 'Google Assistant'}
                  sx={{ width: 20, height: 20 }}
                />
              </IconButton>
            </Tooltip>
          )}
          <Typography variant="body2" sx={{ mr: 1 }}>{track.duration}</Typography>
          <IconButton onClick={(e) => toggleSkip(index, e)}>
            {track.skip ? <UndoIcon /> : <DeleteIcon />}
          </IconButton>
        </ListItemSecondaryAction>
      </ListItem>
    </div>
  );
}

function App() {
  // ---------------------------
  // Dark mode state (default on)
  // ---------------------------
  const [darkMode, setDarkMode] = useState(true);
  const theme = createTheme({ palette: { mode: darkMode ? 'dark' : 'light' } });

  // ---------------------------
  // Track list state with skip flag and persistence
  // ---------------------------
  const defaultTracks = [
    {
      id: '1',
      title: 'Sunscreen (Reapply)',
      src: '/dermusic/audio/Sunscreen-reapply.mp3',
      artist: 'Bravia Dermatology',
      appleMusicUrl: 'https://music.apple.com/ie/album/sunscreen-reapply/1826749295',
      spotifyUrl: 'https://open.spotify.com/track/1Gd0kQCdJeF3i6B7uI0ViB',
      pandoraUrl: 'https://www.pandora.com/artist/bravia-dermatology/sunscreen-reapply/ALJf2q7k52tptp9',
      youtubeMusicUrl: 'https://music.youtube.com/watch?v=AjoD8eyml2g',
      amazonMusicUrl: 'https://music.amazon.com/albums/B0FHJ9Q3CH',
      siriTitle: 'Sunscreen',
      lyrics: `<p>The best sunscreen is the one you use every day<br/>
  that is SPF 30 or higher<br/>
  and reapplied at least every 2 hours</p>
  
  <p>or more frequently if you're sweating<br/>
  swimming, or outside for more than 2 hours.</p>
  
  <p>Reapply, reapply, reapply</p>
  
  <p>Yes, you should wear it every day on exposed skin like the face and ears</p>
  
  <p>Reapply, reapply, reapply</p>
  
  <p>Yes you should wear sunscreen in the winter</p>
  
  <p>When you're wearing short sleeves or shorts, you should wear sunscreen on your arms and legs</p>
  
  <p>Reapply, reapply, reapply</p>
  
  <p>Yes you should wear sunscreen when it’s cloudy or rainy</p>
  
  <p>The best sunscreen is the one you'll actually use every day</p>
  
  <p>Reapply, reapply, reapply</p>
  
  <p>Wide brimmed hats and sunglasses are also recommended to protect your face, scalp, ears and eyes.</p>
  
  <p>Reapply, reapply, reapply</p>`,
      lyricImage: '/dermusic/images/Sunscreen.jpg',
      duration: '--:--',
      skip: false,
    },
    {
      id: '2',
      title: 'Easy as ABCDE',
      src: '/dermusic/audio/Easy_as_ABCDE.mp3',
      artist: 'Bravia Dermatology',
      appleMusicUrl: 'https://music.apple.com/ie/album/easy-as-abcde-melanoma-features/1826644848',
      spotifyUrl: 'https://open.spotify.com/track/2ZMD99ychvN756XPh6firS',
      pandoraUrl: 'https://www.pandora.com/artist/bravia-dermatology/easy-as-abcde-melanoma-features/ALzJgPk4bVtz6tg',
      youtubeMusicUrl: 'https://music.youtube.com/watch?v=1y3GUBzpDpM',
      amazonMusicUrl: 'https://music.amazon.com/albums/B0FHJCNL9G',
      siriTitle: 'Easy as ABCDE',
      lyrics: `<p>For melanoma, early detection is the key<br/>
  Remember it's easy as ABCDE</p>
  
  <p>A is for Asymmetry, a warning sign you see,<br/>
  if one side doesn't match the other, check it carefully.</p>
  
  <p>B is for the Borders, when they're jagged, rough, or wild,<br/>
  not smooth and neat like circles, go get that mole profiled</p>
  
  <p>Early detection is the key<br/>
  remember it's easy as ABCDE</p>
  
  <p>C is for the Color, when it's not just brown or tan,<br/>
  if it's black, blue, red or changes hue, it's time to make a plan.</p>
  
  <p>D is for the Diameter, bigger than a pea,<br/>
  or larger than a pencil eraser, that's a warning you may see.</p>
  
  <p>Early detection is the key<br/>
  Remember it's easy as ABCDE</p>
  
  <p>E is for Evolving, when things begin to change,<br/>
  If it's itching, bleeding, growing - that's abnormal and not in range.<br/>
  [Get it checked if something seems strange]</p>
  
  <p>Early detection is the key<br/>
  Remember it's easy as ABCDE</p>
  
  <p>Skin's your largest organ, Spot the signs<br/>
  Check your skin, it's worth your time.</p>
  
  <p>For bleeding spots of those blue, red and black<br/>
  See your dermatologist - they've got your back</p>
  
  <p>For melanoma, early detection is the key<br/>
  Remember it's easy as ABCDE</p>`,
      lyricImage: '/dermusic/images/Easy_as_ABCDE.jpg',
      duration: '--:--',
      skip: false,
    },
    {
      id: '3',
      title: 'Hero Skin - Firefighter and Police skin cancer checks',
      src: '/dermusic/audio/Hero_skin.mp3',
      artist: 'Bravia Dermatology',
      lyrics: `<p>They serve in sun, in heat, in flame,<br/>
  But silent risks still stake their claim.<br/>
  For heroes who guard us night and day,<br/>
  Let's help protect them in our way.</p>
  
  <p>Shield the sun, check their skin.<br/>
  That's where true care begins.<br/>
  At Bravia in our Ohio town,<br/>
  We keep our heroes safe and sound.<br/>
  Firefighters, police - we see,<br/>
  Your health's our highest priority.</p>
  
  <p>The fire roars, the sirens cry,<br/>
  But cancer creeps where eyes pass by.<br/>
  For every badge and every hose,<br/>
  There's risk to skin, your dermatologist knows</p>
  
  <p>Let's honor them with what we do,<br/>
  A check-up could save more than a few.<br/>
  From early checks to safer days,<br/>
  We give back care in simple ways.</p>
  
  <p>So here's our pledge, here's our vow<br/>
  We stand behind our heroes now.</p>
  
  <p>To our heroes,<br/>
  don't make excuses or an apology.<br/>
  Get your skin checked at Bravia Dermatology.</p>`,
      lyricImage: '/dermusic/images/heroskin.jpg',  // Change if the actual filename is different
      duration: '--:--',
      skip: false,
    },
  ];
  
  const [tracks, setTracks] = useState(defaultTracks);
  const metadataCacheRef = useRef({});

  useEffect(() => {
    let initial = defaultTracks;
    const storedTracks = localStorage.getItem('tracks');
    if (storedTracks) {
      try {
        initial = JSON.parse(storedTracks);
      } catch {
        /* ignore */
      }
    }

    const cached = localStorage.getItem('metadataCache');
    if (cached) {
      try {
        metadataCacheRef.current = JSON.parse(cached);
        initial = initial.map(t => ({ ...t, ...metadataCacheRef.current[t.id] }));
        Object.keys(metadataCacheRef.current).forEach(id => loadedTagIds.current.add(id));
      } catch {
        /* ignore */
      }
    }

    setTracks(initial);
  }, []);

  useEffect(() => {
    localStorage.setItem('tracks', JSON.stringify(tracks));
  }, [tracks]);

  const updateMetadataCache = (id, data) => {
    metadataCacheRef.current[id] = {
      ...metadataCacheRef.current[id],
      ...data,
    };
    localStorage.setItem('metadataCache', JSON.stringify(metadataCacheRef.current));
  };

  const cacheAudio = (src) => {
    if ('caches' in window && src) {
      caches.open('audio-cache').then(cache => {
        cache.match(src).then(res => {
          if (!res) cache.add(src).catch(() => {});
        });
      });
    }
  };

  const prefetchedIds = useRef(new Set());
  const prefetchTrack = (index) => {
    const t = tracks[index];
    if (!t || prefetchedIds.current.has(t.id)) return;
    cacheAudio(t.src);
    prefetchedIds.current.add(t.id);
  };


  // ---------------------------
  // Publication Card data and affiliation logos
  // ---------------------------

  const affiliationLogos = [
    { src: '/logos/bravia-dermatology.svg', alt: 'Bravia Dermatology', url: 'https://braviaderm.com' },
    { src: '/logos/university-of-toledo.svg', alt: 'Division of Dermatology, University of Toledo College of Medicine and Life Sciences', url: 'https://health.utoledo.edu/clinics/dermatology'},
    { src: '/logos/anatomy-mapper.svg', alt: 'Anatomy Mapper', url: 'https://edu.anatomymapper.com/' },
    { src: '/logos/cleveland-clinic.svg', alt: 'Department of Dermatology, Cleveland Clinic', url: 'https://my.clevelandclinic.org/departments/dermatology-plastic-surgery' },
    { src: '/logos/umississippi-ummc.svg', alt: 'Department of Dermatology, University of Mississippi Medical Center', url: 'https://umc.edu/Healthcare/Dermatology/' },
  ];

  // ---------------------------
  // Player state and ID3 tag info
  // ---------------------------
  const audioRef = useRef(null);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [shuffleMode, setShuffleMode] = useState(false);
  const [loopMode, setLoopMode] = useState('off');
  const [tagTitle, setTagTitle] = useState(defaultTracks[0]?.title || '');
  const [tagArtist, setTagArtist] = useState(defaultTracks[0]?.artist || '');
  const [tagArtwork, setTagArtwork] = useState(defaultTracks[0]?.lyricImage || null);
  const loadedTagIds = useRef(new Set());
  const initialMetadataLoaded = useRef(false);

  // ---------------------------
  // UI state for Lyrics and Video Modal
  // ---------------------------
  const [lyricsExpanded, setLyricsExpanded] = useState(true);
  const [openVideoModal, setOpenVideoModal] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [assistantPrompt, setAssistantPrompt] = useState({ open: false, track: null });
  const [assistantType, setAssistantType] = useState(null);

  useEffect(() => {
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    if (/iPhone|iPad|iPod|Mac/i.test(ua)) {
      setAssistantType('siri');
    } else if (/Android/i.test(ua)) {
      setAssistantType('google');
    }
  }, []);

  const openAssistantPrompt = (track) => setAssistantPrompt({ open: true, track });
  const closeAssistantPrompt = () => setAssistantPrompt({ open: false, track: null });

  // Derived current track info
  const currentTrack = tracks[currentTrackIndex];
  const audioTitle = tagTitle || currentTrack?.title || 'No Track';
  const audioArtist = tagArtist || currentTrack?.artist || '--';


  // ---------------------------
  // Setup event listeners to track play/pause on audio element.
  // ---------------------------
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, []);

  // ---------------------------
  // Pause background music when the YouTube modal opens.
  // ---------------------------
  useEffect(() => {
    if (openVideoModal && audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  }, [openVideoModal]);
  // ---------------------------
  // Sync Media Session metadata & playbackState
  // ---------------------------
  useEffect(() => {
  if ('mediaSession' in navigator && tagTitle) {
    navigator.mediaSession.metadata = new MediaMetadata({
      title:    tagTitle,
      artist:   tagArtist,
      artwork:  tagArtwork
        ? [{ src: tagArtwork, sizes: '512x512', type: 'image/png' }]
        : []
    });
    // reflect play/pause on lock screen controls
    navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
  }
}, [tagTitle, tagArtist, tagArtwork, isPlaying]);
  // ---------------------------
  // Change track and defer playback logic to effect.
  // ---------------------------
  const autoPlayRef = useRef(false);
  const handlePlayTrack = (trackIndex, forcePlay = false) => {
    const track = tracks[trackIndex];
    if (!track) return;
    if (track.skip) {
      const nextIndex = getNextPlayableTrack(trackIndex + 1);
      if (nextIndex !== -1) handlePlayTrack(nextIndex, forcePlay);
      return;
    }
    const audio = audioRef.current;
    const wasPlaying = audio && !audio.paused;
    autoPlayRef.current = forcePlay || wasPlaying;
    setCurrentTrackIndex(trackIndex);
  };

  // ---------------------------
  // When currentTrackIndex changes, load metadata and start playback.
  // ---------------------------
  useEffect(() => {
    const track = tracks[currentTrackIndex];
    const audio = audioRef.current;
    if (!track || !audio) return;

    audio.src = track.src;
    cacheAudio(track.src);
    audio.load();
    if (autoPlayRef.current) {
      const playPromise = audio.play();
      if (playPromise) {
        playPromise.catch(() => {
          audio.addEventListener('canplay', () => audio.play().catch(() => {}), { once: true });
        });
      }
    }

    setTagTitle(track.title);
    setTagArtist(track.artist);
    if (track.lyricImage) setTagArtwork(track.lyricImage);

    if (!loadedTagIds.current.has(track.id)) {
      loadedTagIds.current.add(track.id);
      readID3Tags(track, currentTrackIndex);
    }

    const updateDuration = () => {
      const newDuration = formatTime(audio.duration);
      setTracks((prev) => {
        const updated = [...prev];
        updated[currentTrackIndex] = { ...updated[currentTrackIndex], duration: newDuration };
        return updated;
      });
      updateMetadataCache(track.id, { duration: newDuration });
    };

    audio.addEventListener('loadedmetadata', updateDuration, { once: true });

    if (window.gtag) {
      window.gtag('event', 'play_track', {
        track_title: track.title,
        track_id: track.id,
      });
    }

    return () => {
      audio.removeEventListener('loadedmetadata', updateDuration);
    };
  }, [currentTrackIndex]);

  // ---------------------------
  // Audio ended: play next unskipped track.
  // ---------------------------
  const handleEnded = () => {
    const nextIndex = getNextPlayableTrack(currentTrackIndex + 1);
    if (nextIndex !== -1) handlePlayTrack(nextIndex, true);
  };

  // ---------------------------
  // Play All: start playback from the first unskipped track.
  // If already on that track, restart it.
  // ---------------------------
  const handlePlayAll = () => {
    const first = getNextPlayableTrack(0);
    if (first === -1) return;
    const audio = audioRef.current;
    if (currentTrackIndex !== first) {
      handlePlayTrack(first, true);
    } else if (audio) {
      audio.currentTime = 0;
      audio.play().catch(() => {});
    }
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTimeUpdate = () => {
      if (audio.paused || !isFinite(audio.duration)) return;
      const remaining = audio.duration - audio.currentTime;
      if (remaining < 10) {
        const nextIndex = getNextPlayableTrack(currentTrackIndex + 1);
        prefetchTrack(nextIndex);
      }
    };
    audio.addEventListener('timeupdate', onTimeUpdate);
    return () => audio.removeEventListener('timeupdate', onTimeUpdate);
  }, [currentTrackIndex, tracks]);

  // ---------------------------
  // Lazy-load ID3 tags using fetch (as Blob)
  // ---------------------------
  const readID3Tags = (track, trackIndex) => {
    fetch(track.src)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.blob();
      })
      .then((blob) => {
        jsmediatags.read(blob, {
            onSuccess: ({ tags }) => {
              const title   = tags.title  || track.title;
              const artist  = tags.artist || track.artist;
              let   artworkUrl = track.lyricImage || null;

              if (tags.picture) {
                const base64String = tags.picture.data
                  .reduce((data, byte) => data + String.fromCharCode(byte), '');
                artworkUrl = `data:${tags.picture.format};base64,${btoa(base64String)}`;
              }

              setTagTitle(title);
              setTagArtist(artist);
              if (artworkUrl) {
                setTagArtwork(artworkUrl);
                setTracks((prev) => {
                  const updated = [...prev];
                  updated[trackIndex] = { ...updated[trackIndex], lyricImage: artworkUrl };
                  return updated;
                });
              }
              updateMetadataCache(track.id, {
                title,
                artist,
                lyricImage: artworkUrl,
              });
            },
            onError: (error) => {
              console.error('ID3 read error:', error);
            },
          });

      })
      .catch((error) => console.error('Error fetching MP3 file:', error));
  };

  // Preload metadata for the first track on initial load so duration is known.
  useEffect(() => {
    if (initialMetadataLoaded.current) return;
    if (!tracks[0]) return;
    initialMetadataLoaded.current = true;

    if (!loadedTagIds.current.has(tracks[0].id)) {
      loadedTagIds.current.add(tracks[0].id);
      readID3Tags(tracks[0], 0);
    }

    if (!tracks[0].duration || tracks[0].duration === '--:--') {
      const tempAudio = new Audio();
      tempAudio.preload = 'metadata';
      tempAudio.src = tracks[0].src;
      tempAudio.addEventListener('loadedmetadata', () => {
        const newDuration = formatTime(tempAudio.duration);
        setTracks((prev) => {
          const updated = [...prev];
          updated[0] = { ...updated[0], duration: newDuration };
          return updated;
        });
        updateMetadataCache(tracks[0].id, { duration: newDuration });
      });
      tempAudio.load();
    }
  }, [tracks]);

  // ---------------------------
  // Helper: Get next playable (unskipped) track index.
  // ---------------------------
  const getNextPlayableTrack = (startIndex) => {
    const total = tracks.length;
    for (let i = 0; i < total; i++) {
      const idx = (startIndex + i) % total;
      if (!tracks[idx].skip) return idx;
    }
    return -1;
  };

  // ---------------------------
  // Toggle skip (soft-delete) state for a track.
  // ---------------------------
  const toggleSkip = (index, event) => {
    event.stopPropagation();
    setTracks(prev => {
      const newTracks = [...prev];
      newTracks[index].skip = !newTracks[index].skip;
      return newTracks;
    });
  };

  // ---------------------------
  // dnd-kit Drag & Drop – handleDragEnd to update track order.
  // ---------------------------
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = tracks.findIndex(t => t.id === active.id);
      const newIndex = tracks.findIndex(t => t.id === over.id);
      setTracks((items) => arrayMove(items, oldIndex, newIndex));
    }
  };

  // ---------------------------
  // Helper: Update playback rate (state and audio element).
  // ---------------------------

  // ---------------------------
  // Toggle Play/Pause on the current audio track.
  // If no track is loaded, start playing the first track.
  // ---------------------------
  const togglePlayPause = () => {
    if (!audioRef.current) return;
    if (!audioRef.current.src || audioRef.current.src.trim() === "") {
      // No track loaded, play first track.
      handlePlayTrack(0, true);
      return;
    }
    if (audioRef.current.paused) {
      audioRef.current.play().catch(() => {});
    } else {
      audioRef.current.pause();
    }
  };

  // ---------------------------
  // Toggle UI states.
  // ---------------------------
  const toggleLyricsExpanded = () => setLyricsExpanded(prev => !prev);
  const toggleShuffle = () => setShuffleMode(prev => !prev);
  const cycleLoopMode = () => {
    if (loopMode === 'off') {
      setLoopMode('track');
      if (audioRef.current) audioRef.current.loop = true;
    } else if (loopMode === 'track') {
      setLoopMode('playlist');
      if (audioRef.current) audioRef.current.loop = false;
    } else {
      setLoopMode('off');
      if (audioRef.current) audioRef.current.loop = false;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* Outer Box with blue background filling the viewport */}
      <Box sx={{ backgroundColor: "#1f6baf", minHeight: "100vh", display: "flex", justifyContent: "center", alignItems: "center", p: 2 }}>
        {/* Inner Container representing the app; uses theme background and is centered */}
        <Container maxWidth="md" sx={{ backgroundColor: 'background.paper', borderRadius: 2, p: 2 }}>
          {/* Header: Title and Dark/Light Mode Toggle */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h4">🎧 Dermusic Playlist</Typography>
            <IconButton onClick={() => setDarkMode(!darkMode)} color="inherit">
              {darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
          </Box>

          {/* Audio Player Card (at the top below the header) */}
          <Card sx={{ mb: 2 }}>
            <CardContent sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
            {tagArtwork && (
              <CardMedia
                component="img"
                image={tagArtwork}
                alt="Album Art"
                sx={{
                  width: 80,
                  height: 80,
                  objectFit: 'cover',
                  borderRadius: 2,
                  mb: { xs: 2, md: 0 },
                  mx: { xs: 'auto', md: 0 },
                  mr: { md: 2 },
                }}
              />
            )}
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle1" align="center" noWrap>
                <strong>{audioTitle}</strong>
              </Typography>
              <Typography variant="subtitle2" align="center" color="text.secondary" noWrap>
                {audioArtist}
              </Typography>
              <audio
                ref={audioRef}
                src={tracks[currentTrackIndex].src}
                preload="auto"
                onEnded={handleEnded}
                crossOrigin="anonymous"
                style={{ width: '100%', marginTop: 8 }}
                controls
                controlsList="nodownload"
              />
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1 }}>
                  {currentTrack.appleMusicUrl && (
                    <Tooltip title={`Listen to ${currentTrack.title} on Apple Music`}>
                      <IconButton component="a" href={currentTrack.appleMusicUrl} target="_blank" rel="noopener noreferrer">
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            bgcolor: 'white',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Box component="img" src="/logos/apple-music.svg" alt="Apple Music" sx={{ width: 24, height: 24 }} />
                        </Box>
                      </IconButton>
                    </Tooltip>
                  )}
                  {currentTrack.spotifyUrl && (
                    <Tooltip title={`Listen to ${currentTrack.title} on Spotify`}>
                      <IconButton component="a" href={currentTrack.spotifyUrl} target="_blank" rel="noopener noreferrer">
                        <Box component="img" src="/logos/spotify.svg" alt="Spotify" sx={{ width: 24, height: 24 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {currentTrack.pandoraUrl && (
                    <Tooltip title={`Listen to ${currentTrack.title} on Pandora`}>
                      <IconButton component="a" href={currentTrack.pandoraUrl} target="_blank" rel="noopener noreferrer">
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            bgcolor: 'white',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Box component="img" src="/logos/pandora.svg" alt="Pandora" sx={{ width: 24, height: 24 }} />
                        </Box>
                      </IconButton>
                    </Tooltip>
                  )}
                  {currentTrack.youtubeMusicUrl && (
                    <Tooltip title={`Listen to ${currentTrack.title} on YouTube Music`}>
                      <IconButton component="a" href={currentTrack.youtubeMusicUrl} target="_blank" rel="noopener noreferrer">
                        <Box component="img" src="/logos/youtube-music.svg" alt="YouTube Music" sx={{ width: 24, height: 24 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {currentTrack.amazonMusicUrl && (
                    <Tooltip title={`Listen to ${currentTrack.title} on Amazon Music`}>
                      <IconButton component="a" href={currentTrack.amazonMusicUrl} target="_blank" rel="noopener noreferrer">
                        <Box component="img" src="/logos/amazon-music.svg" alt="Amazon Music" sx={{ height: 24, width: 'auto' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {assistantType && currentTrack.siriTitle && (
                    <Tooltip
                      title={
                        assistantType === 'siri'
                          ? `Just say \"Hey Siri, listen to ${currentTrack.siriTitle} by ${currentTrack.artist}\" to play on Apple Music`
                          : `Just say \"Hey Google, play ${currentTrack.siriTitle} by ${currentTrack.artist}\"`
                      }
                    >
                      <IconButton onClick={() => openAssistantPrompt(currentTrack)}>
                        <Box
                          component="img"
                          src={assistantType === 'siri' ? '/logos/siri.svg' : '/logos/google-assistant.svg'}
                          alt={assistantType === 'siri' ? 'Siri' : 'Google Assistant'}
                          sx={{ width: 24, height: 24 }}
                        />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
              </CardContent>
            </Card>

          {/* Playback Controls (below the player) */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap', mb: 3 }}>
            <Tooltip title="Previous Song">
              <IconButton color="primary" onClick={() => handlePlayTrack((currentTrackIndex - 1 + tracks.length) % tracks.length)}>
                <SkipPreviousIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Play All (skips skipped songs)">
              <Button
                variant="outlined"
                startIcon={<PlayArrowIcon />}
                onClick={handlePlayAll}
              >
                Play All
              </Button>
            </Tooltip>
            <Tooltip title="Next Song">
              <IconButton color="primary" onClick={() => handlePlayTrack((currentTrackIndex + 1) % tracks.length)}>
                <SkipNextIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Play / Pause">
              <IconButton color="primary" onClick={togglePlayPause}>
                {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Toggle Shuffle">
              <IconButton onClick={toggleShuffle} sx={{ color: shuffleMode ? 'secondary.main' : 'inherit' }}>
                <ShuffleIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={`Loop Mode: ${loopMode}`}>
              <IconButton onClick={cycleLoopMode} sx={{ color: loopMode !== 'off' ? 'secondary.main' : 'inherit' }}>
                <LoopIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Song List – dnd-kit sortable list (scrollable if over 4 items) */}
          <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={tracks.map(track => track.id)}>
              <List sx={{
                backgroundColor: theme.palette.background.default,
                borderRadius: 2,
                p: 1,
                mb: 2,
                maxHeight: 300,
                overflowY: 'auto'
              }}>
                {tracks.map((track, index) => (
                  <SortableItem
                    key={track.id}
                    id={track.id}
                    track={track}
                    index={index}
                    currentTrackIndex={currentTrackIndex}
                    handlePlayTrack={handlePlayTrack}
                    toggleSkip={toggleSkip}
                    openAssistantPrompt={openAssistantPrompt}
                    assistantType={assistantType}
                  />
                ))}
              </List>
            </SortableContext>
          </DndContext>

          {/* Collapsible Lyrics Card */}
          <Card sx={{ mb: 2 }}>
            <CardHeader
              title={`Lyrics for "${audioTitle}" by "${audioArtist}"`}
              titleTypographyProps={{ variant: 'h6' }}
              action={
                <IconButton onClick={toggleLyricsExpanded}>
                  <ExpandMoreIcon sx={{ transform: lyricsExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }} />
                </IconButton>
              }
              avatar={currentTrack?.lyricImage ? (
                <CardMedia
                  component="img"
                  image={currentTrack.lyricImage}
                  alt="Lyric Thumbnail"
                  sx={{ width: 50, height: 50, borderRadius: '50%' }}
                />
              ) : null}
            />
            <Collapse in={lyricsExpanded} timeout="auto" unmountOnExit>
              <CardContent>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 1 }}>
                    {currentTrack.appleMusicUrl && (
                      <Tooltip title={`Listen to ${currentTrack.title} on Apple Music`}>
                        <IconButton component="a" href={currentTrack.appleMusicUrl} target="_blank" rel="noopener noreferrer">
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              bgcolor: 'white',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Box component="img" src="/logos/apple-music.svg" alt="Apple Music" sx={{ width: 24, height: 24 }} />
                          </Box>
                        </IconButton>
                      </Tooltip>
                    )}
                    {currentTrack.spotifyUrl && (
                      <Tooltip title={`Listen to ${currentTrack.title} on Spotify`}>
                        <IconButton component="a" href={currentTrack.spotifyUrl} target="_blank" rel="noopener noreferrer">
                          <Box component="img" src="/logos/spotify.svg" alt="Spotify" sx={{ width: 24, height: 24 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {currentTrack.pandoraUrl && (
                      <Tooltip title={`Listen to ${currentTrack.title} on Pandora`}>
                        <IconButton component="a" href={currentTrack.pandoraUrl} target="_blank" rel="noopener noreferrer">
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              bgcolor: 'white',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Box component="img" src="/logos/pandora.svg" alt="Pandora" sx={{ width: 24, height: 24 }} />
                          </Box>
                        </IconButton>
                      </Tooltip>
                    )}
                    {currentTrack.youtubeMusicUrl && (
                      <Tooltip title={`Listen to ${currentTrack.title} on YouTube Music`}>
                        <IconButton component="a" href={currentTrack.youtubeMusicUrl} target="_blank" rel="noopener noreferrer">
                          <Box component="img" src="/logos/youtube-music.svg" alt="YouTube Music" sx={{ width: 24, height: 24 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {currentTrack.amazonMusicUrl && (
                      <Tooltip title={`Listen to ${currentTrack.title} on Amazon Music`}>
                        <IconButton component="a" href={currentTrack.amazonMusicUrl} target="_blank" rel="noopener noreferrer">
                          <Box component="img" src="/logos/amazon-music.svg" alt="Amazon Music" sx={{ height: 24, width: 'auto' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {assistantType && currentTrack.siriTitle && (
                      <Tooltip
                        title={
                          assistantType === 'siri'
                            ? `Just say \"Hey Siri, listen to ${currentTrack.siriTitle} by ${currentTrack.artist}\" to play on Apple Music`
                            : `Just say \"Hey Google, play ${currentTrack.siriTitle} by ${currentTrack.artist}\"`
                        }
                      >
                        <IconButton onClick={() => openAssistantPrompt(currentTrack)}>
                          <Box
                            component="img"
                            src={assistantType === 'siri' ? '/logos/siri.svg' : '/logos/google-assistant.svg'}
                            alt={assistantType === 'siri' ? 'Siri' : 'Google Assistant'}
                            sx={{ width: 24, height: 24 }}
                          />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <div dangerouslySetInnerHTML={{ __html: currentTrack?.lyrics || '' }} />
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    © {new Date().getFullYear()} Bravia Dermatology. All rights reserved.
                  </Typography>
                </Box>
              </CardContent>
            </Collapse>
          </Card>

          {/* Sample Music Video Button (above Publication Card) */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Button variant="contained" onClick={() => setOpenVideoModal(true)}>
              View Sample Music Video
            </Button>
          </Box>

          {/* Publication Card */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                As seen in:
              </Typography>
              <Typography variant="body1" component="div">
              <h1>🎵 AI-generated music promoting medical education. 🎵</h1>
<strong>Authors:</strong> Bryce DeLong, MS<sup>1,2,3</sup>; Melissa Piliang, MD, FAAD<sup>4</sup>; Robert T. Brodell, MD, FAAD<sup>5,6</sup>; Matthew Molenda, MD, FAAD<sup>1,2,3</sup><br />
<strong>Authors affiliations</strong> (at time of publication):<br />
<br />1. Bravia Dermatology, Holland, OH; USA<br />
2. Division of Dermatology, University of Toledo College of Medicine and Life Sciences, Toledo, OH; USA<br />
3. Anatomy Mapper, Holland, OH; USA<br />
4. Department of Dermatology, Cleveland Clinic, Cleveland, OH; USA<br />
5. Department of Pathology, University of Mississippi Medical Center, Jackson, MS; USA<br />
6. Department of Dermatology, University of Mississippi Medical Center, Jackson, MS; USA<br />
<br />
<strong>DOI:</strong>{' '}
<Link href="https://doi.org/10.1016/j.jaad.2025.09.017" target="_blank" rel="noopener noreferrer" color="primary" underline="always">
10.1016/j.jaad.2025.09.017
</Link>
<br />
<strong>Citation:</strong> DeLong B, Piliang M, Brodell RT, Molenda MA. <em>AI-generated music promoting medical education.</em> J Amer Acad Dermatol., Volume TBD, Issue TBD. Accepted September 2025.
              </Typography>
              <Box sx={{ mt: 2 }}>
              <Grid container spacing={2} justifyContent="center">
  {affiliationLogos.map((logo, index) => (
    <Grid item xs={6} md={3} key={index} sx={{ textAlign: 'center' }}>
      <a href={logo.url} target="_blank" rel="noopener noreferrer" style={{ display: 'block' }}>
        <Box
          sx={{
            backgroundColor: '#fff',
            width: { xs: 120, md: 150 },   // Adjust sizes as needed
            height: { xs: 60, md: 75 },
            borderRadius: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            margin: 'auto',
          }}
        >
          <Box
            component="img"
            src={logo.src}
            alt={logo.alt}
            sx={{
              maxWidth: '90%',
              maxHeight: '90%',
            }}
          />
        </Box>
      </a>
    </Grid>
  ))}
</Grid>

              </Box>
            </CardContent>
          </Card>

          {/* Voice Assistant Modal */}
          <Dialog open={assistantPrompt.open} onClose={closeAssistantPrompt}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', pr: 6 }}>
              <Box
                component="img"
                src={assistantType === 'siri' ? '/logos/siri.svg' : '/logos/google-assistant.svg'}
                alt={assistantType === 'siri' ? 'Siri logo' : 'Google Assistant logo'}
                sx={{ width: 24, height: 24, mr: 1 }}
              />
              {assistantType === 'siri' ? 'Siri' : 'Google Assistant'}
              <IconButton
                aria-label="close"
                onClick={closeAssistantPrompt}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ textAlign: 'center' }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                {assistantPrompt.track?.lyricImage && (
                  <Box
                    component="a"
                    href={assistantPrompt.track?.appleMusicUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ display: 'inline-block' }}
                  >
                    <Box
                      component="img"
                      src={assistantPrompt.track.lyricImage}
                      alt={assistantPrompt.track.title}
                      sx={{ width: 80, height: 80, objectFit: 'cover' }}
                    />
                  </Box>
                )}
              </Box>
              <Typography>
                {assistantType === 'siri' ? 'Play this on Apple Music by saying' : 'Play this by saying'}
                <br />
                <Typography component="span" sx={{ fontWeight: 'bold' }}>
                  {assistantType === 'siri'
                    ? `"Hey Siri, play ${assistantPrompt.track?.siriTitle} by ${assistantPrompt.track?.artist}"`
                    : `"Hey Google, play ${assistantPrompt.track?.siriTitle} by ${assistantPrompt.track?.artist}"`}
                </Typography>
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeAssistantPrompt}>Close</Button>
            </DialogActions>
          </Dialog>

          {/* Video Modal */}
          <Dialog open={openVideoModal} onClose={() => setOpenVideoModal(false)} maxWidth="md" fullWidth>
            <DialogTitle>
              Sample Music Video
              <IconButton
                aria-label="close"
                onClick={() => setOpenVideoModal(false)}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
                <iframe
                  title="Music Video"
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                  src="https://www.youtube.com/embed/kXOU0RxAn0A"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </Box>
            </DialogContent>
          </Dialog>
        </Container>
      </Box>
    </ThemeProvider>
  );
}

export default App;
